import { LineItem, SharedDesignOrigin } from "@/models/order/order";
import { ProductType } from "@/models/products/product";

/**
 * FUPOF-193: read the Shade Designer configuration code a line came from.
 *
 * The code is stored in two places on purpose, and this reads both:
 *
 *  - `item_details.sd_share_id` is the persisted copy. `item_details` is a
 *    `nullable|array` column the Laravel API stores and echoes back verbatim,
 *    so a marker inside it round-trips through a saved draft for free. A
 *    top-level line column would instead need a migration, `$fillable`,
 *    StoreQuoteRequest rules and a resource entry in that repo, because both
 *    the request and QuoteController::lineInstanceFields whitelist line keys
 *    explicitly and silently drop anything else.
 *  - `LineItem.sd_share_id` is the in-memory mirror, so store-resident lines
 *    read correctly before they have ever been through a draft round-trip.
 */
export const sharedDesignCode = (item: LineItem): string | null => {
  if (!item) return null;
  if (item.sd_share_id) return item.sd_share_id;
  const details = item.item_details as { sd_share_id?: string } | undefined;
  return details?.sd_share_id ?? null;
};

/**
 * FUPOF-193: whether this line has been changed since it came from its Shade
 * Designer code. Read from both places it lives, like the code itself.
 *
 * The code stays on a line for good — "built from JRODK" is still true after
 * the rep changes it — and this flag says it no longer matches. A line that
 * carries its design's origin (see SharedDesignOrigin) has the flag re-judged
 * on every save, so setting the configuration back clears it. A line added
 * before origins existed has nothing to compare against, so for it the flag
 * stays sticky.
 */
export const sharedDesignAdjusted = (item: LineItem): boolean => {
  if (!item) return false;
  if (item.sd_adjusted) return true;
  const details = item.item_details as { sd_adjusted?: boolean } | undefined;
  return !!details?.sd_adjusted;
};

/** Set or clear the adjusted flag, in both locations. */
const setSharedDesignAdjusted = (item: LineItem, adjusted: boolean): void => {
  const details = item.item_details as { sd_adjusted?: boolean } | undefined;
  if (adjusted) {
    item.sd_adjusted = true;
    if (details) details.sd_adjusted = true;
  } else {
    delete item.sd_adjusted;
    if (details) delete details.sd_adjusted;
  }
};

/**
 * Mark a shared-design line (both locations) as adjusted. A no-op on a line
 * with no code: "adjusted from the design" means nothing without a design.
 */
export const markSharedDesignAdjusted = <T extends LineItem>(item: T): T => {
  if (!sharedDesignCode(item)) return item;
  setSharedDesignAdjusted(item, true);
  return item;
};

/** Stamp a line (both locations) as having come from a shared design. */
export const stampSharedDesign = <T extends LineItem>(
  item: T,
  shareId: string
): T => {
  item.sd_share_id = shareId;
  if (item.item_details) {
    (item.item_details as { sd_share_id?: string }).sd_share_id = shareId;
  }
  return item;
};

/** A base the design named but this catalog couldn't resolve: never matches. */
export const UNRESOLVED_DESIGN_KEY = "unresolved";

export const sharedDesignOrigin = (
  item: LineItem
): SharedDesignOrigin | null => {
  if (!item) return null;
  if (item.sd_origin) return item.sd_origin;
  const details = item.item_details as
    | { sd_origin?: SharedDesignOrigin }
    | undefined;
  return details?.sd_origin ?? null;
};

/** Stamp a line (both locations) with its design's origin. */
export const stampSharedDesignOrigin = <T extends LineItem>(
  item: T,
  origin: SharedDesignOrigin
): T => {
  item.sd_origin = origin;
  if (item.item_details) {
    (item.item_details as {
      sd_origin?: SharedDesignOrigin;
    }).sd_origin = origin;
  }
  return item;
};

/**
 * The part of a base line's configuration a rep chooses: model, finish, stem
 * and wheels. Not configFingerprint — the stored base document carries an
 * `updated_at` that would make an untouched base look edited. Takes the
 * `item_details.base` shape; `baseFinish` may be the Finish object or its
 * handle.
 */
export const baseDesignKey = (base: unknown): string => {
  const b = (base ?? {}) as {
    base?: { model?: string };
    baseFinish?: { handle?: string } | string | null;
    baseStem?: string | null;
    baseOptions?: { wheels?: boolean };
  };
  const finish =
    b.baseFinish && typeof b.baseFinish === "object"
      ? b.baseFinish.handle
      : b.baseFinish;
  return [
    b.base?.model ?? "",
    String(finish ?? ""),
    String(b.baseStem ?? ""),
    b.baseOptions?.wheels ? "W" : ""
  ].join("|");
};

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map(
        key =>
          `${JSON.stringify(key)}:${stableStringify(
            (value as Record<string, unknown>)[key]
          )}`
      );
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value ?? null);
};

/**
 * FUPOF-193: a stable fingerprint of a line's *configuration*, used to tell
 * whether a shared design is still exactly what the code produced.
 *
 * The provenance pill says "this line is Shade Designer K7Q2M". The moment a
 * rep changes anything about the configuration — model, a fabric, a trim style,
 * a finial finish — that stops being exactly true, so the line is marked
 * adjusted (see sharedDesignAdjusted); the code itself stays. The SKU
 * alone can't decide this: it encodes the canopy and fabrics but not the finial,
 * finial finish, bottom pole or spigot, so a line could change materially while
 * its SKU stayed put.
 *
 * `notes`, the marker itself, its `sd_adjusted` flag and `sd_origin`, and
 * the line's `setup_id` are excluded — a note is not configuration, and neither is where
 * the line came from or which setup it belongs to. None of them changes across
 * an edit today, so none could decide the comparison, but none must be able
 * to.
 * Keys are sorted at every level so two equal configurations always fingerprint
 * the same regardless of the order their objects were assembled in.
 */
export const configFingerprint = (details: unknown): string => {
  if (!details || typeof details !== "object") return "";
  const {
    notes,
    sd_share_id,
    sd_adjusted,
    sd_origin,
    setup_id,
    ...config
  } = details as Record<string, unknown>;
  return stableStringify(config);
};

/** A short, stable hash, so a whole umbrella config keys in a few bytes. */
const hashString = (value: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < value.length; i++) {
    const ch = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return hex(h2) + hex(h1);
};

/**
 * FUPOF-193: a line's configuration as one comparable key — what "as
 * designed" is judged on.
 *
 * The model is part of it. It lives on the line, not in `item_details`, so a
 * fingerprint of the details alone missed a model change entirely.
 *  - base: baseDesignKey of its `item_details.base`
 *  - top cover (accessory): its model, which carries the finish
 *  - anything else: the model plus configFingerprint of its details
 */
export const designKey = (item: {
  product_type?: string;
  model?: string;
  item_details?: unknown;
}): string => {
  if (item.product_type === ProductType.Base) {
    return baseDesignKey((item.item_details as { base?: unknown })?.base);
  }
  if (item.product_type === ProductType.Accessory) return item.model ?? "";
  return hashString(
    `${item.model ?? ""}|${configFingerprint(item.item_details)}`
  );
};

/**
 * FUPOF-193: re-judge a shared-design line against its origin and set or
 * clear its adjusted flag accordingly. Each line answers for its own part of
 * the design only; the cart shows the flag per line.
 *
 * @param setupDeviates a part that hangs off this line is missing (see
 *   designBaseMissing / designCoverMissing).
 * @returns false when the line has no code or no origin — nothing was judged,
 *   and the caller falls back to the sticky flag.
 */
export const judgeSharedDesign = (
  item: LineItem,
  setupDeviates = false
): boolean => {
  const origin = sharedDesignOrigin(item);
  if (!sharedDesignCode(item) || !origin) return false;
  setSharedDesignAdjusted(
    item,
    setupDeviates || designKey(item) !== origin.config
  );
  return true;
};

/**
 * A part the design had is gone from the setup. "Adjusted" belongs on the line
 * the missing part hangs off: a missing base on the umbrella, a missing top
 * cover on the base (or on the umbrella, when the base went too — the base
 * check already covers that). A part the design never had is not the parent's
 * business: that line is adjusted on its own.
 *
 * @param origin the umbrella's, which is the one that records the design's
 *   base and cover.
 */
export const designBaseMissing = (
  origin: SharedDesignOrigin | null,
  hasBase: boolean
): boolean => !!origin && origin.base != null && !hasBase;

export const designCoverMissing = (
  origin: SharedDesignOrigin | null,
  hasCover: boolean
): boolean => !!origin && origin.cover != null && !hasCover;
